"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ALL, CLASS_ORDER, LANG_ORDER, cascadingOptions } from "../_filter-utils";
import { IsbnField } from "@/app/_components/IsbnField";
import { formatIsbn } from "@/lib/domain/isbn";

export interface CatalogEntry {
  id: string;
  anScolar: string;
  clasa: string;
  disciplina: string;
  titluManual: string;
  autori: string;
  editura: string;
  nrActAprobare: string;
  modAchizitie: string;
  limba: string;
  isbn: string;
  needsReview: boolean;
}

export function CatalogBrowser({ entries, canEdit }: { entries: CatalogEntry[]; canEdit: boolean }) {
  const [anScolar, setAnScolar] = useState(ALL);
  const [clasa, setClasa] = useState(ALL);
  const [disciplina, setDisciplina] = useState(ALL);
  const [editura, setEditura] = useState(ALL);
  const [limba, setLimba] = useState(ALL);
  const [onlyReview, setOnlyReview] = useState(false);
  const [isbnQuery, setIsbnQuery] = useState("");

  const active = useMemo(
    () => ({ anScolar, clasa, disciplina, limba, editura }),
    [anScolar, clasa, disciplina, limba, editura],
  );
  const fields = useMemo(
    () =>
      entries.map((e) => ({
        anScolar: e.anScolar,
        clasa: e.clasa,
        disciplina: e.disciplina,
        limba: e.limba,
        editura: e.editura,
      })),
    [entries],
  );

  const anScolarOptions = useMemo(() => cascadingOptions(fields, active, "anScolar").reverse(), [fields, active]);
  const clasaOptions = useMemo(() => cascadingOptions(fields, active, "clasa", CLASS_ORDER), [fields, active]);
  const disciplinaOptions = useMemo(() => cascadingOptions(fields, active, "disciplina"), [fields, active]);
  const limbaOptions = useMemo(() => cascadingOptions(fields, active, "limba", LANG_ORDER), [fields, active]);
  const edituraOptions = useMemo(() => cascadingOptions(fields, active, "editura"), [fields, active]);

  const reviewCount = useMemo(() => entries.filter((e) => e.needsReview).length, [entries]);

  const filtered = useMemo(() => {
    return entries.filter(
      (e) =>
        (anScolar === ALL || e.anScolar === anScolar) &&
        (clasa === ALL || e.clasa === clasa) &&
        (disciplina === ALL || e.disciplina === disciplina) &&
        (editura === ALL || e.editura === editura) &&
        (limba === ALL || e.limba === limba) &&
        (!onlyReview || e.needsReview) &&
        (isbnQuery === "" || e.isbn.includes(isbnQuery)),
    );
  }, [entries, anScolar, clasa, disciplina, editura, limba, onlyReview, isbnQuery]);

  return (
    <div>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <FilterSelect label="An școlar" value={anScolar} onChange={setAnScolar} options={anScolarOptions} />
        <FilterSelect label="Clasă" value={clasa} onChange={setClasa} options={clasaOptions} />
        <FilterSelect label="Disciplină" value={disciplina} onChange={setDisciplina} options={disciplinaOptions} />
        <FilterSelect label="Limbă" value={limba} onChange={setLimba} options={limbaOptions} />
        <FilterSelect label="Editură" value={editura} onChange={setEditura} options={edituraOptions} />
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Caută după ISBN</label>
          <IsbnField value={isbnQuery} onChange={setIsbnQuery} placeholder="978-606-…" />
        </div>
      </div>

      {reviewCount > 0 ? (
        <label className="mb-4 flex w-fit items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} />
          Arată doar de verificat ({reviewCount})
        </label>
      ) : null}

      <p className="mb-3 text-sm text-slate-500">
        {filtered.length} din {entries.length} manuale
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((e) => {
          const card = (
            <div
              className={`h-full rounded-xl border bg-white p-4 shadow-sm transition ${
                e.needsReview ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"
              } ${canEdit ? "hover:border-indigo-300 hover:shadow-md" : ""}`}
            >
              {e.needsReview ? (
                <span className="mb-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  ⚠ De verificat
                </span>
              ) : null}
              <p className="font-medium text-slate-900">{e.titluManual}</p>
              <p className="mt-1 text-sm text-slate-500">{e.autori || "—"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                  Clasa {e.clasa}
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  {e.disciplina}
                </span>
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  {e.limba}
                </span>
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                  {e.anScolar}
                </span>
              </div>
              <div className="mt-3 space-y-0.5 text-xs text-slate-500">
                <p>{e.editura}</p>
                {e.nrActAprobare ? <p>Act aprobare: {e.nrActAprobare}</p> : null}
                {e.modAchizitie ? <p>Achiziție: {e.modAchizitie}</p> : null}
                {e.isbn ? <p>ISBN: {formatIsbn(e.isbn)}</p> : null}
              </div>
              {canEdit ? <p className="mt-3 text-xs font-medium text-indigo-600">Editează →</p> : null}
            </div>
          );
          return canEdit ? (
            <Link key={e.id} href={`/evidenta/catalog/${e.id}`}>
              {card}
            </Link>
          ) : (
            <div key={e.id}>{card}</div>
          );
        })}
        {filtered.length === 0 ? (
          <p className="col-span-full py-8 text-center text-sm text-slate-400">Niciun manual găsit.</p>
        ) : null}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      >
        <option value={ALL}>Toate</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
